/** Visitor-local calendar days, independent of Shopify product and cart state. */
export function shippingDate(now, offset = 5) {
  const date = new Date(now);
  if (!Number.isFinite(date.getTime())) return null;
  const number = offset == null || offset === '' ? 5 : Number(offset);
  const days = Number.isFinite(number) ? Math.max(0, Math.min(30, Math.trunc(number))) : 5;
  // Noon keeps the calendar calculation clear of most daylight-saving transitions.
  date.setHours(12, 0, 0, 0);
  date.setDate(date.getDate() + days);
  return date;
}

export function shippingDateText(date, locale = 'fr-FR', weekday = true, month = true) {
  if (!date || !Number.isFinite(date.getTime())) return '';
  const options = { day: 'numeric', ...(weekday ? { weekday: 'short' } : {}), ...(month ? { month: 'short' } : {}) };
  try {
    const requested = locale || 'fr-FR';
    const supported = Intl.DateTimeFormat.supportedLocalesOf(requested).length ? requested : 'fr-FR';
    return new Intl.DateTimeFormat(supported, options).format(date);
  }
  catch { return new Intl.DateTimeFormat('fr-FR', options).format(date); }
}

if (globalThis.customElements && !customElements.get('posea-shipping-date')) {
  class PoseaShippingDate extends HTMLElement {
    static observedAttributes = ['data-offset', 'data-locale', 'data-weekday', 'data-month'];
    #timer;
    #controller;
    #observer;

    connectedCallback() {
      this.#controller?.abort();
      this.#controller = new AbortController();
      const options = { signal: this.#controller.signal };
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.refresh(); }, options);
      window.addEventListener('pageshow', () => this.refresh(), options);
      window.addEventListener('focus', () => this.refresh(), options);
      // Combined listings can morph our children while retaining this element.
      this.#observer?.disconnect();
      this.#observer = new MutationObserver(() => { if (this.isConnected) this.refresh(); });
      this.#observe();
      queueMicrotask(() => { if (this.isConnected) this.refresh(); });
    }

    disconnectedCallback() {
      clearTimeout(this.#timer);
      this.#controller?.abort();
      this.#observer?.disconnect();
    }

    attributeChangedCallback() { if (this.isConnected) this.refresh(); }

    #observe() {
      this.#observer?.observe(this, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'data-date-ready'] });
    }

    refresh() {
      clearTimeout(this.#timer);
      const now = new Date();
      const date = shippingDate(now, this.dataset.offset);
      const value = shippingDateText(date, this.dataset.locale, this.dataset.weekday !== 'false', this.dataset.month !== 'false');
      const output = this.querySelector('time');
      const content = this.querySelector('[data-shipping-content]');
      const fallback = this.querySelector('[data-shipping-fallback]');
      if (value && output && content && fallback) {
        this.#observer?.disconnect();
        output.textContent = value;
        // Local date components, not toISOString(), which converts to UTC.
        output.dateTime = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        content.hidden = false;
        fallback.setAttribute('aria-hidden', 'true');
        this.setAttribute('data-date-ready', '');
        this.#observe();
      }
      const next = new Date(now);
      next.setHours(24, 0, 0, 100);
      this.#timer = setTimeout(() => this.refresh(), Math.max(1000, next.getTime() - now.getTime()));
    }
  }
  customElements.define('posea-shipping-date', PoseaShippingDate);
}
